/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useMemo, useEffect } from 'react';
import Model, { helper } from '@tripian/model';
import moment from 'moment';
import Button from '../base/Button/Button';
import { Calendar, Clock, ChevronRight, X } from '../base/Svg/Icons';
import classes from './TimelineAddPoiModal.scss';

// Generate time options between start and end times
const generateTimeRange = (startTime: string, endTime: string): string[] => {
  const times: string[] = [];
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);

  let currentHour = startHour;
  let currentMin = startMin;

  while (currentHour < endHour || (currentHour === endHour && currentMin <= endMin)) {
    const hourStr = currentHour.toString().padStart(2, '0');
    const minStr = currentMin.toString().padStart(2, '0');
    times.push(`${hourStr}:${minStr}`);

    currentMin += 30;
    if (currentMin >= 60) {
      currentMin = 0;
      currentHour += 1;
    }
  }

  return times;
};

interface ITimelineAddPoiModal {
  poi: Model.Poi;
  plans: Model.Plan[];
  currentDayIndex: number;
  onAdd: (planId: number, poiId: string, from?: string, to?: string) => void;
  onClose: () => void;
  t: (value: Model.TranslationKey) => string;
}

const TimelineAddPoiModal: React.FC<ITimelineAddPoiModal> = ({ poi, plans, currentDayIndex, onAdd, onClose, t }) => {
  const [selectedDayIndex, setSelectedDayIndex] = useState<number>(currentDayIndex);
  const [fromTime, setFromTime] = useState<string>('');
  const [toTime, setToTime] = useState<string>('');

  const selectedPlan = plans[selectedDayIndex];

  // Generate time options based on selected plan's start/end times
  const timeOptions = useMemo(() => {
    if (!selectedPlan) return [];
    return generateTimeRange(selectedPlan.startTime, selectedPlan.endTime);
  }, [selectedPlan]);

  // Filter "to" options to only show times after selected "from" time
  const toTimeOptions = useMemo(() => {
    if (!fromTime) return timeOptions;
    const fromIndex = timeOptions.indexOf(fromTime);
    return timeOptions.slice(fromIndex + 1);
  }, [timeOptions, fromTime]);

  // Reset times when day changes
  useEffect(() => {
    setFromTime('');
    setToTime('');
  }, [selectedDayIndex]);

  // Auto-set "to" time when "from" changes
  useEffect(() => {
    if (fromTime && (!toTime || timeOptions.indexOf(toTime) <= timeOptions.indexOf(fromTime))) {
      const fromIndex = timeOptions.indexOf(fromTime);
      if (fromIndex < timeOptions.length - 1) {
        setToTime(timeOptions[fromIndex + 2] || timeOptions[fromIndex + 1]);
      }
    }
  }, [fromTime, timeOptions, toTime]);

  const handleApply = () => {
    if (selectedPlan) {
      onAdd(selectedPlan.id, poi.id, fromTime || undefined, toTime || undefined);
    }
  };

  return (
    <div
      className={classes.overlay}
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      {/* Backdrop */}
      <div className={classes.backdrop} />

      {/* Modal */}
      <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button onClick={onClose} className={classes.closeButton}>
          <X fill="#6B7280" className={classes.closeIcon} />
        </button>

        {/* POI Header */}
        <div className={classes.header}>
          {poi.image?.url ? (
            <div className={classes.imageContainer}>
              <img src={helper.imgUrl(poi.image.url, 400, 160)} alt={poi.name} className={classes.image} />
              <div className={classes.imageGradient} />
            </div>
          ) : (
            <div className={classes.noImageHeader} />
          )}

          {/* POI Info */}
          <div className={`${poi.image?.url ? classes.poiInfoOverlay : classes.poiInfoNoImage}`}>
            <h3 className={`${classes.poiName} ${poi.image?.url ? classes.poiNameWhite : classes.poiNameDark}`}>{poi.name}</h3>
            {poi.category && poi.category.length > 0 && (
              <p className={`${classes.poiCategory} ${poi.image?.url ? classes.poiCategoryWhite : classes.poiCategoryDark}`}>{poi.category[0].name}</p>
            )}
          </div>
        </div>

        {/* Content */}
        <div className={classes.content}>
          {/* Day Selection */}
          <div>
            <label className={classes.sectionLabel}>
              <span className={classes.sectionIcon}>
                <Calendar fill="var(--primary-color)" />
              </span>
              {t('trips.myTrips.itinerary.day')}
            </label>
            <div className={classes.dayGrid}>
              {plans.map((plan, index) => (
                <button
                  key={plan.id}
                  onClick={() => setSelectedDayIndex(index)}
                  className={`${classes.dayButton} ${selectedDayIndex === index ? classes.dayButtonActive : classes.dayButtonInactive}`}
                >
                  <span className={`${classes.dayNumber} ${selectedDayIndex === index ? classes.dayNumberActive : classes.dayNumberInactive}`}>
                    {index + 1}
                  </span>
                  {plan.date && (
                    <span className={`${classes.dayDate} ${selectedDayIndex === index ? classes.dayDateActive : classes.dayDateInactive}`}>
                      {moment(plan.date).format('DD MMM')}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Time Selection */}
          <div>
            <div className={classes.timeHeader}>
              <label className={classes.sectionLabel}>
                <span className={classes.sectionIcon}>
                  <Clock fill="var(--primary-color)" />
                </span>
                Time Range
              </label>
              {selectedPlan && (
                <span className={classes.timeRange}>
                  {selectedPlan.startTime} - {selectedPlan.endTime}
                </span>
              )}
            </div>
            <div className={classes.timeSelectors}>
              <div className={classes.timeSelectWrapper}>
                <select className={classes.timeSelect} value={fromTime} onChange={(e) => setFromTime(e.target.value)}>
                  <option value="">--:--</option>
                  {timeOptions.slice(0, -1).map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>

              <div className={classes.timeArrow}>
                <ChevronRight stroke="var(--primary-color)" />
              </div>

              <div className={classes.timeSelectWrapper}>
                <select
                  className={`${classes.timeSelect} ${!fromTime ? classes.timeSelectDisabled : ''}`}
                  value={toTime}
                  onChange={(e) => setToTime(e.target.value)}
                  disabled={!fromTime}
                >
                  <option value="">--:--</option>
                  {toTimeOptions.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={classes.footer}>
          <Button
            color="primary"
            text={t('trips.myTrips.itinerary.step.addToItinerary.submit.apply')}
            onClick={handleApply}
            className={classes.applyButton}
          />
        </div>
      </div>
    </div>
  );
};

export default TimelineAddPoiModal;